/**
 * @module public/js/core/roleLoader
 * @summary Browser helpers for loading the signed-in user's tenant roles.
 */

import {
  getDoc,
  getDocs,
} from 'https://www.gstatic.com/firebasejs/9.17.2/firebase-firestore.js';
import {
  colGlobal,
  docGlobal,
  docWithTenant,
} from '../utils/firestoreHelpers.js';
import { auth } from '/js/core/firebase-service.js';
import {
  onAuthStateChanged,
  onIdTokenChanged,
} from 'https://www.gstatic.com/firebasejs/9.17.2/firebase-auth.js';
import {
  derivePrimaryTenantRole,
  normalizeTenantRoles,
} from './tenantRoles.js';
import { isTenantActive } from '../utils/tenantLifecycle.js';

let roleStateCache = null;
let legacyRoleShapeCache = null;
let adminTenantCache = null;
let authListenerBound = false;

/**
 * Return any tenant identifiers the browser is explicitly trying to view.
 *
 * This stays intentionally narrow: we only trust the currently selected tenant
 * candidates that the UI already persists in storage, and we still verify the
 * viewer's admin membership with a direct `Users/{uid}` lookup before exposing
 * them as accessible admin tenants.
 *
 * @returns {string[]} Unique stored tenant identifiers.
 */
function getStoredTenantCandidates() {
  const local =
    typeof window !== 'undefined'
      ? window.localStorage?.getItem?.('activeTenant') || null
      : null;
  const session =
    typeof window !== 'undefined'
      ? window.sessionStorage?.getItem?.('topsail__tenant') || null
      : null;

  return [
    ...new Set(
      [local, session]
        .map((tenantId) => String(tenantId || '').trim())
        .filter(Boolean)
    ),
  ];
}

/**
 * Filter a tenant id list down to tenants whose root lifecycle remains active.
 *
 * @param {string[]} tenantIds - Candidate tenant identifiers.
 * @param {Map<string, Record<string, any>>} [prefetchedTenantData] - Optional tenant root payloads by id.
 * @returns {Promise<string[]>} Active tenant identifiers.
 */
async function filterActiveTenantIds(
  tenantIds,
  prefetchedTenantData = new Map()
) {
  const uniqueTenantIds = [
    ...new Set(
      tenantIds.filter(
        (tenantId) => typeof tenantId === 'string' && tenantId.trim()
      )
    ),
  ];
  const results = await Promise.all(
    uniqueTenantIds.map(async (tenantId) => {
      const prefetched = prefetchedTenantData.get(tenantId);
      if (prefetched && !isTenantActive(prefetched)) {
        return null;
      }
      if (prefetched && isTenantActive(prefetched)) {
        return tenantId;
      }

      try {
        const snap = await getDoc(docGlobal(`tenants/${tenantId}`));
        const data = snap.exists() ? snap.data() || {} : null;
        return isTenantActive(data) ? tenantId : null;
      } catch (err) {
        console.warn('[roleLoader] failed to read tenant lifecycle', {
          tenantId,
          message: err?.message || String(err),
        });
        return null;
      }
    })
  );

  return results.filter(Boolean);
}

/**
 * Register a one-time auth listener that clears cached role state whenever the
 * signed-in Firebase user changes.
 *
 * @returns {void}
 */
function bindAuthInvalidation() {
  if (authListenerBound) return;
  const clearCache = () => {
    roleStateCache = null;
    legacyRoleShapeCache = null;
    adminTenantCache = null;
  };
  onAuthStateChanged(auth, clearCache);
  onIdTokenChanged(auth, clearCache);
  authListenerBound = true;
}

/**
 * Resolve the current Firebase Auth user, waiting briefly when bootstrapping.
 *
 * @returns {Promise<import('firebase/auth').User>}
 * @throws {Error} When no signed-in user or tenant context is available.
 */
async function getCurrentUser() {
  bindAuthInvalidation();

  if (auth.currentUser) {
    return auth.currentUser;
  }

  return new Promise((resolve, reject) => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (!user) return;
      unsub();
      resolve(user);
    });

    window.setTimeout(() => {
      unsub();
      reject(new Error('Sign-in required'));
    }, 5_000);
  });
}

/**
 * Determine whether a claims payload contains the global `super_admin` grant.
 *
 * @param {Record<string, any>|undefined} claims - Firebase Auth custom claims.
 * @returns {boolean} True when the claims include `super_admin`.
 */
function hasSuperAdminClaim(claims) {
  return claims?.roles?.super_admin === true || claims?.role === 'super_admin';
}

/**
 * Load the current viewer's tenant-scoped role state from `Users/{uid}`.
 *
 * Tenant roles come from Firestore only. The sole global privilege handled
 * here is the `super_admin` custom claim.
 *
 * @returns {Promise<{
 *   uid: string,
 *   tenantId: string,
 *   role: string,
 *   roles: string[],
 *   isAdmin: boolean,
 *   isManager: boolean,
 *   isSuperAdmin: boolean,
 * }>}
 */
export async function getCurrentUserRoleState() {
  if (roleStateCache) return roleStateCache;

  const user = await getCurrentUser();
  const tokenResult = await user.getIdTokenResult();
  const claims = tokenResult?.claims || {};
  const tenantId = user.tenantId || claims.tenantId || '';

  if (!tenantId) {
    console.error('[roleLoader] missing tenant ID');
    window.location.href = '/index.html';
    throw new Error('Missing tenant ID');
  }

  let roles = [];
  try {
    const snap = await getDoc(docWithTenant(tenantId, `Users/${user.uid}`));
    const data = snap.exists() ? snap.data() : {};
    roles = normalizeTenantRoles(data?.roles, data?.role);
  } catch (err) {
    console.warn('[roleLoader] failed to read tenant user roles', err);
  }

  const isSuperAdmin = hasSuperAdminClaim(claims);
  roleStateCache = {
    uid: user.uid,
    tenantId,
    role: derivePrimaryTenantRole(roles),
    roles,
    isAdmin: isSuperAdmin || roles.includes('admin'),
    isManager: roles.includes('manager'),
    isSuperAdmin,
  };
  return roleStateCache;
}

/**
 * Return the legacy `{ admins, managers }` shape used by existing admin pages.
 *
 * This compatibility helper now derives membership from the signed-in user's
 * tenant user document instead of `AppConfig/roles`.
 *
 * @returns {Promise<{
 *   admins: string[],
 *   managers: string[],
 *   role: string,
 *   roles: string[],
 *   isAdmin: boolean,
 *   isManager: boolean,
 *   isSuperAdmin: boolean,
 * }>}
 */
export async function getAppRoles() {
  if (legacyRoleShapeCache) return legacyRoleShapeCache;

  const state = await getCurrentUserRoleState();
  legacyRoleShapeCache = {
    admins: state.isAdmin ? [state.uid] : [],
    managers: state.isManager ? [state.uid] : [],
    role: state.role,
    roles: state.roles,
    isAdmin: state.isAdmin,
    isManager: state.isManager,
    isSuperAdmin: state.isSuperAdmin,
  };
  return legacyRoleShapeCache;
}

/**
 * Resolve the list of tenant IDs the current viewer may administer.
 *
 * Regular admins are resolved through direct `Users/{uid}` reads for the
 * current and explicitly selected tenants. `super_admin` may access all
 * active tenant roots.
 *
 * @returns {Promise<string[]>} Ordered list of accessible tenant IDs.
 */
export async function getAccessibleAdminTenants() {
  if (adminTenantCache) return adminTenantCache;

  const state = await getCurrentUserRoleState();
  if (state.isSuperAdmin) {
    try {
      const snap = await getDocs(colGlobal('tenants'));
      const prefetchedTenantData = new Map(
        snap.docs.map((docSnap) => [docSnap.id, docSnap.data() || {}])
      );
      adminTenantCache = await filterActiveTenantIds(
        snap.docs.map((docSnap) => docSnap.id),
        prefetchedTenantData
      );
      if (adminTenantCache.length > 0) return adminTenantCache;
    } catch (err) {
      console.warn('[roleLoader] failed to load super-admin tenant list', err);
    }
  }

  try {
    const candidates = [
      ...new Set(
        [state.tenantId]
          .concat(getStoredTenantCandidates())
          .filter((tenantId) => typeof tenantId === 'string' && tenantId.trim())
      ),
    ];

    const matchedTenantIds = [];
    for (const tenantId of candidates) {
      try {
        const snap = await getDoc(
          docWithTenant(tenantId, `Users/${state.uid}`)
        );
        const data = snap.exists() ? snap.data() || {} : {};
        const roles = normalizeTenantRoles(data?.roles, data?.role);
        if (roles.includes('admin')) {
          matchedTenantIds.push(tenantId);
        }
      } catch (err) {
        console.warn('[roleLoader] failed to read tenant admin membership', {
          tenantId,
          message: err?.message || String(err),
        });
      }
    }

    adminTenantCache = await filterActiveTenantIds(
      matchedTenantIds.length > 0
        ? matchedTenantIds
        : state.isAdmin
          ? [state.tenantId]
          : []
    );
  } catch (err) {
    console.warn(
      '[roleLoader] failed to resolve accessible admin tenants',
      err
    );
    adminTenantCache = state.isAdmin
      ? await filterActiveTenantIds([state.tenantId])
      : [];
  }

  return adminTenantCache;
}
